import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { IconDownload, IconTrash } from "@tabler/icons";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useFileJobs } from "src/hooks/useFileJobs";

const statusColors = {
  pending: "warning",
  processing: "info",
  completed: "success",
  failed: "error",
};

const JobItem = ({ job, onDelete }) => {
  const theme = useTheme();
  const { downloadFile, deleteJob } = useFileJobs();
  const [currentProgress, setCurrentProgress] = useState(job.progress || 0);

  useEffect(() => {
    if (job?.progress !== undefined && job.progress !== currentProgress) {
      setCurrentProgress(job.progress);
    }
  }, [job?.progress]);

  const handleDownload = async () => {
    try {
      await downloadFile(job.jobId);
      toast.success(`Downloaded ${job.outputFileName || job.fileName}`);
    } catch (error) {
      toast.error("Failed to download file");
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete(job.jobId);
    } else {
      try {
        await deleteJob(job.jobId);
        toast.success("Job deleted successfully");
      } catch (error) {
        toast.error("Failed to delete job");
      }
    }
  };

  const formatTime = (date) => {
    try {
      return format(new Date(date), "HH:mm");
    } catch {
      return "--:--";
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case "completed":
        return theme.palette.success.main;
      case "failed":
        return theme.palette.error.main;
      case "processing":
        return theme.palette.info.main;
      default:
        return theme.palette.warning.main;
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          boxShadow: theme.shadows[2],
          borderColor: theme.palette.primary.light,
        },
      }}
    >
      <Stack spacing={1.5}>
        {/* Header with status and time */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Chip
            label={job.status}
            color={statusColors[job.status]}
            size="small"
            variant="outlined"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {formatTime(job.createdAt)}
          </Typography>
        </Box>

        {/* File name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {job.fileName}
        </Typography>

        {/* File type */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: "capitalize",
            fontWeight: 500,
          }}
        >
          {job.fileType?.replace("_", " ")}
        </Typography>

        {/* Progress section */}
        {job.status === "completed" ? (
          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: theme.palette.success.light,
              "& .MuiLinearProgress-bar": {
                bgcolor: theme.palette.success.main,
                borderRadius: 4,
              },
            }}
          />
        ) : job.status === "processing" ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {job.currentStep || "Processing..."}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: getProgressColor(job.status) }}
              >
                {currentProgress || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={currentProgress || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: theme.palette.grey[200],
                "& .MuiLinearProgress-bar": {
                  bgcolor: getProgressColor(job.status),
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        ) : null}

        {/* Error message for failed jobs */}
        {job.status === "failed" && job.error && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              fontStyle: "italic",
              bgcolor: theme.palette.error.light,
              p: 1,
              borderRadius: 1,
            }}
          >
            {job.error}
          </Typography>
        )}

        {/* Action buttons for completed jobs */}
        {job.status === "completed" && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              size="small"
              onClick={handleDownload}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: theme.palette.primary.light,
                },
              }}
              title="Download file"
            >
              <IconDownload size={16} />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: theme.palette.error.main,
                "&:hover": {
                  bgcolor: theme.palette.error.light,
                },
              }}
              title="Delete job"
            >
              <IconTrash size={16} />
            </IconButton>
          </Box>
        )}

      </Stack>
    </Box>
  );
};

export default JobItem;
